import { useAuthStore } from './auth.js';
import { useSuspensionStore } from './suspension.js';
import { useBillingBlockStore } from './billing-block.js';
import type { ApiErrorBody, AuthSuccess } from '../types/api.js';

const BASE = '/api/v1';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string;
  public readonly field?: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.field = body.error.field;
  }
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** When true, will not attempt a refresh on 401 (avoids loops on the refresh call itself). */
  noRetry?: boolean;
};

// Coordinate concurrent 401s so we only refresh once.
let inflightRefresh: Promise<string | null> | null = null;

async function tryRefresh(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  inflightRefresh = (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        useAuthStore.getState().clear();
        return null;
      }
      const data = (await res.json()) as AuthSuccess;
      useAuthStore.getState().setSession(data.accessToken, data.user);
      return data.accessToken;
    } catch {
      useAuthStore.getState().clear();
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = useAuthStore.getState().accessToken;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
    body: opts.body != null ? JSON.stringify(opts.body) : undefined,
  });

  if (res.status === 401 && !opts.noRetry) {
    const newToken = await tryRefresh();
    if (newToken) {
      // Retry once with the fresh token.
      const retry = await fetch(`${BASE}${path}`, {
        method: opts.method ?? 'GET',
        headers: { ...headers, Authorization: `Bearer ${newToken}` },
        credentials: 'include',
        body: opts.body != null ? JSON.stringify(opts.body) : undefined,
      });
      return handleResponse<T>(retry);
    }
  }

  return handleResponse<T>(res);
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (res.ok) {
    // A successful call means access is restored — clear any stale access flags
    // (e.g. after a super-admin reactivates them, or after they subscribe).
    if (useSuspensionStore.getState().suspended) useSuspensionStore.getState().setSuspended(false);
    if (useBillingBlockStore.getState().blocked) useBillingBlockStore.getState().setBlocked(false);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }
  let body: ApiErrorBody;
  try {
    body = (await res.json()) as ApiErrorBody;
  } catch {
    body = { error: { code: 'NETWORK', message: res.statusText } };
  }
  // The backend gates block every request for a suspended (403) or billing-lapsed
  // (402) agency. Flip the matching global flag (keyed on the code) so the
  // top-level screen takes over instead of surfacing a raw error. The two are
  // kept mutually exclusive: a 402 proves the suspension gate let it through, and
  // a suspension supersedes a mere billing block.
  if (body.error.code === 'ACCOUNT_SUSPENDED') {
    useSuspensionStore.getState().setSuspended(true);
    useBillingBlockStore.getState().setBlocked(false);
  } else if (body.error.code === 'PAYMENT_REQUIRED') {
    useBillingBlockStore.getState().setBlocked(true);
    useSuspensionStore.getState().setSuspended(false);
  }
  throw new ApiError(res.status, body);
}

/** Convenience wrappers. */
export const apiGet = <T>(path: string) => api<T>(path, { method: 'GET' });
export const apiPost = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', body });
export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body });
export const apiPut = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', body });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** A 2FA-enabled user's first login step returns this instead of a session. */
export type TwoFactorChallenge = { twoFactorRequired: true; pendingToken: string };
export type LoginResult = AuthSuccess | TwoFactorChallenge;

/** Login is special — it must not attempt to refresh on 401 (no session yet). */
export async function loginRequest(email: string, password: string): Promise<LoginResult> {
  return api<LoginResult>('/auth/login', {
    method: 'POST',
    body: { email, password },
    noRetry: true,
  });
}

/** Second login step for 2FA users: exchange the pending token + code for a session. */
export async function loginTwoFactorRequest(
  pendingToken: string,
  code: string,
): Promise<AuthSuccess> {
  return api<AuthSuccess>('/auth/login/2fa', {
    method: 'POST',
    body: { pendingToken, code },
    noRetry: true,
  });
}

/** Logged-out forgot-password request. Never refreshes on 401 (no session). */
export async function requestPasswordReset(email: string): Promise<{ ok: true }> {
  return api<{ ok: true }>('/auth/forgot-password', {
    method: 'POST',
    body: { email },
    noRetry: true,
  });
}

/** Logged-out reset step. Returns success only — the user then signs in afresh. */
export async function resetPassword(token: string, newPassword: string): Promise<{ ok: true }> {
  return api<{ ok: true }>('/auth/reset-password', {
    method: 'POST',
    body: { token, newPassword },
    noRetry: true,
  });
}

export async function logoutRequest(): Promise<void> {
  await api<{ ok: true }>('/auth/logout', { method: 'POST', noRetry: true }).catch(() => {});
  useAuthStore.getState().clear();
}

/**
 * Attempt to restore a session from the refresh cookie on app boot.
 * Returns true if a session was restored.
 */
export async function bootSession(): Promise<boolean> {
  const token = await tryRefresh();
  return token !== null;
}
